import api from './api';

export interface NacExceptionItem {
  id: number;
  company?: string;
  department?: string;
  name?: string;
  ip?: string;
  exceptionItem?: string;
  applyDate?: string;
  startDate?: string;
  endDate?: string;
  reason?: string;
  approvalDocNo?: string;
  remark?: string;
  actionType?: string;
}

export interface NacReportSummary {
  id: number;
  period: string;
  companyName: string;
  summaryContent: string | null;
  updateUser: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface NodeStatus1Item {
  node_type: string;
  current_count: number;
  prev_count: number;
  diff: number;
  diff_text: string;
  ratio: string;
}

export interface NodeStatus2Item {
  policy_name: string;
  current_count: number;
  prev_count: number;
  diff: number;
  diff_text: string;
  ratio: string;
}

export interface NodeStatus3Item {
  os_category: string;
  current_count: number;
  prev_count: number;
  diff: number;
  diff_text: string;
  ratio: string;
}

export interface NodeStatus4Item {
  platform: string;
  current_count: number;
  prev_count: number;
  diff: number;
  diff_text: string;
  ratio: string;
}

export interface NacNodeData {
  node_type: string;           // 노드타입
  sensor_status: string;       // 센서동작
  agent_status: string;        // 에이전트 동작
  status: string;              // 동작 (UP/DOWN)
  ipv4: string;                // IPv4주소
  mac: string;                 // MAC주소
  username: string;            // 사용자명
  dept: string;                // 부서명
  location: string;            // 현위치
  policy: string;              // 노드정책
  control_policy: string;      // 제어정책
  hostname: string;            // 호스트명
  platform: string;            // 플랫폼
  sensor: string;              // 센서명
  registered_at: string;       // 등록시각
  last_auth: string;           // 최근 사용자인증
  auth_username: string;       // 인증사용자명
  auth_userid: string;         // 인증사용자ID
}

export interface NacDataResponse {
  data: NacNodeData[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const nacService = {
  // Period 관련
  getAvailablePeriods: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/nac/periods');
    return response.data;
  },

  // 인가된 회사 목록
  getPermittedCompanies: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/nac/report/permitted-companies');
    return response.data;
  },

  // Report Summary
  getSummary: async (period: string, companyName: string): Promise<NacReportSummary | null> => {
    const response = await api.get('/nac/report/summary', {
      params: { period, companyName },
    });
    return response.data;
  },

  // IPT-556 이슈3 — 보고서 예외처리 이력 (viewer 접근, 선택 회사 전체)
  getReportExceptions: async (companyName: string): Promise<NacExceptionItem[]> => {
    const response = await api.get<NacExceptionItem[]>('/nac/report/exceptions', {
      params: { companyName },
    });
    return response.data;
  },

  saveSummary: async (
    period: string,
    companyName: string,
    summaryContent: string,
  ): Promise<NacReportSummary> => {
    const response = await api.post('/nac/report/summary', {
      period,
      companyName,
      summaryContent,
    });
    return response.data;
  },

  // 노드현황 1: NAC 전체노드
  getNodeStatus1: async (
    period: string,
    companyName: string,
    prevPeriod?: string,
  ): Promise<NodeStatus1Item[]> => {
    const response = await api.get<NodeStatus1Item[]>('/nac/report/node-status-1', {
      params: { period, companyName, prevPeriod },
    });
    return response.data;
  },

  // 노드현황 2: PC별 정책 현황
  getNodeStatus2: async (
    period: string,
    companyName: string,
    prevPeriod?: string,
  ): Promise<NodeStatus2Item[]> => {
    const response = await api.get<NodeStatus2Item[]>('/nac/report/node-status-2', {
      params: { period, companyName, prevPeriod },
    });
    return response.data;
  },

  // 노드현황 3: PC별 운영체제 요약
  getNodeStatus3: async (
    period: string,
    companyName: string,
    prevPeriod?: string,
  ): Promise<NodeStatus3Item[]> => {
    const response = await api.get<NodeStatus3Item[]>('/nac/report/node-status-3', {
      params: { period, companyName, prevPeriod },
    });
    return response.data;
  },

  // 노드현황 4: 운영체제별 PC 노드
  getNodeStatus4: async (
    period: string,
    companyName: string,
    prevPeriod?: string,
  ): Promise<NodeStatus4Item[]> => {
    const response = await api.get<NodeStatus4Item[]>('/nac/report/node-status-4', {
      params: { period, companyName, prevPeriod },
    });
    return response.data;
  },

  // 비고 관리
  getStatNotes: async (
    period: string,
    companyName: string,
    statType: string,
  ): Promise<Record<string, string>> => {
    const response = await api.get<Record<string, string>>('/nac/report/stat-notes', {
      params: { period, companyName, statType },
    });
    return response.data;
  },

  saveStatNote: async (
    period: string,
    companyName: string,
    statType: string,
    statKey: string,
    note: string,
  ): Promise<any> => {
    const response = await api.post('/nac/report/stat-notes', {
      period,
      companyName,
      statType,
      statKey,
      note,
    });
    return response.data;
  },

  // Publish 관련
  getPublishStatus: async (period: string, companyName: string): Promise<boolean> => {
    const response = await api.get<{ published: boolean }>('/nac/report/publish-status', {
      params: { period, companyName },
    });
    return response.data.published;
  },

  // NAC 데이터 뷰
  getNacData: async (
    period: string,
    company: string,
    page: number = 1,
    limit: number = 50,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    search?: string,
  ): Promise<NacDataResponse> => {
    const response = await api.get<NacDataResponse>('/nac/data', {
      params: { period, company, page, limit, sortBy, sortOrder, search },
    });
    return response.data;
  },

  exportNacData: async (period: string, company: string): Promise<void> => {
    const response = await api.get('/nac/data/export', {
      params: { period, company },
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `NAC_${company}_${period}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

